// @flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import theme from './FileDropzone.scss';
import Dropzone from './Dropzone';
import { listCardFactory } from 'common/components/core/card/ListCard';
import { SpanLoader } from 'common/components/core/wrappers/Span';
import { EventKeyEnum } from 'common/enums/DomEnums';
import { translate, Trans } from 'react-i18next';
import coreIds from 'common/tests/core/coreIds.json';
import batchIds from 'common/tests/batchScreen/batchIds.json';

type Props = {
    theme: Object,
    fileDropTitle?: string,
    accept?: string | Array<string>,
    className?: string,
    maxSize: ?number,
    inlineDropzone: boolean,
    disableActiveStyling: boolean,
    isGetTemplateLoading?: boolean,
    onGetTemplateClick?: () => void,
    onDrop: () => void,
    parse?: string,
    onFileUploadStart: ({ file: File }) => void,
    onFileUploadSuccess: ({
        file: File,
        parsedFile: { fileRows: Array<Object>, fileHeaders: Array<string> }
    }) => void,
    onFileUploadError: ({ file: File, uploadErrors: Array<string> }) => void,
    testId?: string,
    transformHeader?: string => string,
    actions?: Array<{ label: string, onClick: () => void }>
};

const FileDropSubtitle = ({ theme, openFileDialog, openFileDialogKeyDown }) => {
    return (
        <Trans i18nKey="orBrowseToUpload" className={theme.subtitle}>
            <span>or</span>{' '}
            <span
                testId={batchIds.uploadBatch.browseButton}
                tabIndex={2}
                onClick={openFileDialog}
                onKeyDown={openFileDialogKeyDown}
                className={theme.highlight}
            >
                browse
            </span>{' '}
            to upload
        </Trans>
    );
};

const FolderIcon = ({ theme }) => (
    <img src="/img/folder.svg" className={theme.folderIcon} />
);

const DefaultFileDropCard = listCardFactory({
    Subtitle: FileDropSubtitle,
    LeftIcon: FolderIcon
});
const fileDropzoneFactory = ({ FileDropCard = DefaultFileDropCard }) => {
    class FileDropzone extends React.PureComponent<Props, void> {
        getDropzoneContainer = (ref: Object): void => {
            this.dropzone = ref;
        };

        openFileDialog = (): void => {
            this.dropzone && this.dropzone.open();
        };

        openFileDialogKeyDown = event => {
            if (event.key === EventKeyEnum.Enter) {
                this.openFileDialog();
            }
        };

        downloadTemplateFileKeyDown = event => {
            if (event.key === EventKeyEnum.Enter) {
                const { onGetTemplateClick } = this.props;
                onGetTemplateClick();
            }
        };

        render() {
            const {
                t,
                theme,
                accept,
                isGetTemplateLoading,
                onGetTemplateClick,
                fileDropTitle = t('dropFile'),
                onDrop,
                className,
                parse,
                onFileUploadStart,
                onFileUploadSuccess,
                onFileUploadError,
                isUploadInProgress,
                inlineDropzone,
                disableActiveStyling,
                disableRejectStyling,
                maxSize,
                transformHeader,
                actions,
                ...others
            } = this.props;
            return (
                <Dropzone
                    accept={accept}
                    multiple={false}
                    testId={coreIds.fileDropZone}
                    maxSize={maxSize}
                    getDropzoneContainer={this.getDropzoneContainer}
                    onDrop={onDrop}
                    disableClick
                    theme={theme}
                    className={classnames(theme.fileDropzone, className, {
                        [theme.inlineDropzone]: inlineDropzone
                    })}
                    parse={parse}
                    onFileUploadStart={onFileUploadStart}
                    onFileUploadSuccess={onFileUploadSuccess}
                    onFileUploadError={onFileUploadError}
                    disableActiveStyling={disableActiveStyling}
                    disableRejectStyling={disableRejectStyling}
                    transformHeader={transformHeader}
                >
                    <div className={theme.dropContainer}>
                        <div className={theme.dropBackground}>
                            <FileDropCard
                                theme={theme}
                                className={theme.fileDropCard}
                                title={fileDropTitle}
                                isUploadInProgress={isUploadInProgress}
                                openFileDialogKeyDown={
                                    this.openFileDialogKeyDown
                                }
                                openFileDialog={this.openFileDialog}
                                {...others}
                            />
                        </div>
                    </div>
                    {onGetTemplateClick ? (
                        <SpanLoader
                            testId={batchIds.uploadBatch.getTemplate}
                            tabIndex={3}
                            loading={isGetTemplateLoading}
                            onClick={onGetTemplateClick}
                            onKeyDown={this.downloadTemplateFileKeyDown}
                            className={classnames(
                                theme.templateBtn,
                                theme.highlight
                            )}
                        >
                            {t('getTemplate')}
                        </SpanLoader>
                    ) : null}
                    {actions?.map((action, index) => {
                        return (
                            <span
                                key={index}
                                className={classnames(
                                    theme.templateBtn,
                                    theme.highlight
                                )}
                                onClick={action.onClick}
                            >
                                {t(action.label)}
                            </span>
                        );
                    })}
                </Dropzone>
            );
        }
    }
    return translate()(themr('FileDropzone', theme)(FileDropzone));
};

const ThemedFileDropzone = fileDropzoneFactory({});
export { ThemedFileDropzone as FileDropzone, fileDropzoneFactory };
export default ThemedFileDropzone;
